import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { useRenewalQuotePreview, useCreateRenewalQuote, useRetagQuoteAsRecurring } from '../hooks/useSubscriptions';
import type { RenewalQuoteLine } from '../types';

interface RenewalQuotePanelProps {
  subscriptionId: string | null;
  /** #1407: jump to the Lines tab from the lines_unverified warning; absent outside the subscription modal. */
  onGoToLines?: () => void;
}

function lineTotal(l: RenewalQuoteLine) {
  return (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * (1 - (Number(l.discount) || 0) / 100);
}

/**
 * Renewal quote preview + one-click generation (S8 UI). Shows the proposed quote
 * — copied from the reference recurring invoice with ATEK-SA/CS/SIM quantities
 * recomputed — and a Generate button that POSTs the create. Read-only until the
 * AM clicks Generate; nothing is written on load.
 */
export default function RenewalQuotePanel({ subscriptionId, onGoToLines }: RenewalQuotePanelProps) {
  const { t } = useTranslation('subscriptions');
  const { data, isLoading, isError } = useRenewalQuotePreview(subscriptionId);
  const createMutation = useCreateRenewalQuote();
  const retagMutation = useRetagQuoteAsRecurring();

  if (!subscriptionId) return null;

  if (isLoading) {
    return <Skeleton className="h-40 w-full" data-testid="renewal-quote-loading" />;
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        {t('renewalQuote.loadError', 'Could not load the renewal quote preview.')}
      </p>
    );
  }

  // #1406 — the panel blocks only when there is NEITHER a reference invoice
  // (recurring, pure-service, or purchase fallback) NOR verified subscription
  // lines: verified lines are a complete renewal on their own, no copy needed.
  if (!data.referenceInvoice && data.source !== 'lines') {
    return (
      <p className="text-sm italic text-muted-foreground" data-testid="renewal-quote-no-reference">
        {t('renewalQuote.noReference', 'No recurring reference invoice and no verified subscription lines yet — a renewal quote cannot be generated.')}
      </p>
    );
  }

  const created = createMutation.data;

  return (
    <div className="space-y-3" data-testid="renewal-quote">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="h-4 w-4" />
          {t('renewalQuote.title', 'Renewal quote preview')}
        </h4>
        <Button
          type="button"
          size="sm"
          data-testid="renewal-quote-generate"
          disabled={createMutation.isPending || !!created}
          onClick={() => createMutation.mutate(subscriptionId)}
        >
          {createMutation.isPending
            ? t('renewalQuote.generating', 'Generating…')
            : t('renewalQuote.generate', 'Generate quote')}
        </Button>
      </div>

      {created && (
        <div
          className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
          data-testid="renewal-quote-created"
        >
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span>
            {t('renewalQuote.created', 'Renewal quote {{num}} created', { num: created.quote.quoteNumber })}
            {created.linkedSensors ? ` · ${t('renewalQuote.linkedSensors', '{{n}} sensors linked', { n: created.linkedSensors })}` : ''}
          </span>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('renewalQuote.col.sku', 'SKU')}</TableHead>
            <TableHead className="text-right">{t('renewalQuote.col.qty', 'Qty')}</TableHead>
            <TableHead className="text-right">{t('renewalQuote.col.unit', 'Unit')}</TableHead>
            <TableHead className="text-right">{t('renewalQuote.col.discount', 'Disc %')}</TableHead>
            <TableHead className="text-right">{t('renewalQuote.col.total', 'Total')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-testid="renewal-quote-lines">
          {data.lines.map((l, i) => (
            <TableRow key={`${l.sku}-${i}`} data-testid="renewal-quote-line">
              <TableCell className="font-medium">
                {l.sku}
                {l.kind === 'prorated' && <Badge variant="secondary" className="ml-2">{t('renewalQuote.prorated', 'pro-rata')}</Badge>}
              </TableCell>
              <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(l.unitPrice)}</TableCell>
              <TableCell className="text-right tabular-nums">{l.discount || 0}</TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(lineTotal(l))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-end gap-2 text-sm font-semibold" data-testid="renewal-quote-total">
        <span className="text-muted-foreground">{t('renewalQuote.estimatedTotal', 'Estimated total')}:</span>
        <span className="tabular-nums">{formatCurrency(data.total)}</span>
      </div>

      {data.warnings.length > 0 && (
        <ul className="space-y-1" data-testid="renewal-quote-warnings">
          {data.warnings.map((w, i) => (
            <li key={`${w.code}-${i}`} className="flex items-start gap-2 text-xs text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{t(`renewalQuote.warnings.${w.code}`, w.code)}</span>
              {/* #1397 D3 — "all covered" is only actionable next to the window it is
                  covered FOR, so show the dates. They ride beside the sentence, not
                  inside it: this t() call takes no options object, so a {{placeholder}}
                  in the copy would render literally (guarded by the locale parity spec).
                  Dates are data anyway — ISO, identical in both locales. */}
              {w.code === 'sa_line_dropped_all_covered' && !!data.window?.start && !!data.window?.end && (
                <span className="shrink-0 font-mono tabular-nums" data-testid="renewal-quote-window">
                  {data.window.start} → {data.window.end}
                </span>
              )}
              {/* Smart-picker companion: the stale-reference warning carries the
                  quote behind the newest invoice — offer the one-click confirm
                  (tag it recurring) so the user decides, not the system. */}
              {w.code === 'reference_invoice_stale' && !!w.detail?.newestQuoteId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  data-testid="renewal-quote-retag"
                  disabled={retagMutation.isPending}
                  onClick={() => retagMutation.mutate(String(w.detail?.newestQuoteId))}
                >
                  {retagMutation.isPending
                    ? t('renewalQuote.retagging', 'Marking…')
                    : t('renewalQuote.retag', 'Mark quote {{num}} as recurring', { num: String(w.detail?.newestQuoteNumber ?? '') })}
                </Button>
              )}
              {/* #1407 — the fix for unverified lines lives one tab over: jump
                  there. Not for reason 'no_lines' (zero ACTIVE lines): there is
                  nothing to verify on the Lines tab — the fix is to ADD a line. */}
              {w.code === 'lines_unverified' && w.detail?.reason !== 'no_lines' && onGoToLines && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-6 shrink-0 px-0 text-xs"
                  data-testid="renewal-quote-goto-lines"
                  onClick={onGoToLines}
                >
                  {t('renewalQuote.goToLines', 'Go to Lines to verify')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
